/**
 * FixedListSelect.jsx
 * -------------------
 * A native <select> over a FIXED vocabulary (e.g. refdata-oem-global). The
 * user can only choose a value from `options`; nothing is typed, and blank is
 * never choosable. Built for the OEM fixed-list arc; generic on purpose.
 *
 * Unlike ComboBox (free text + suggestions), this enforces three rules:
 *   1. An OFF-LIST stored value is never an <option> — not enabled, not
 *      disabled, nowhere in the option set — so it cannot be selected,
 *      scrolled past, or become vocabulary by sitting there.
 *   2. It renders as READ-ONLY TEXT beside the control, outside the <select>.
 *   3. Off-list = case-sensitive membership in `options`. 'Toyota' against
 *      'TOYOTA' is off-list: it shows read-only and the user picks TOYOTA.
 *
 * The placeholder <option> is DISABLED: it displays for a blank value but
 * cannot be chosen, and onChange never fires with ''.
 *
 * React fallback, handled explicitly: React 18 selects the FIRST ENABLED
 * option when a controlled <select>'s value matches none — with a disabled
 * placeholder that would be the first real option, showing e.g. ADIENT for a
 * stored TEST. So an off-list value is never handed to the <select>; the
 * control is pinned to the placeholder and the stored value is shown as text.
 *
 * Empty `options` (still loading, or the fetch failed) is NOT treated as
 * "everything is off-list": the stored value shows read-only without the
 * off-list note, and the control is disabled because there is nothing to
 * choose.
 *
 * Props:
 *   value       {string|null} — stored value
 *   onChange    {fn}          — called with the chosen option string only
 *   options     {string[]}    — the fixed list
 *   placeholder {string}      — text of the disabled blank option
 *   className   {string}      — applied to the <select> (caller's input style)
 *   disabled    {boolean}
 *   layout      {'stack'|'inline'} — read-only text below (default) or beside
 *   selectRef   {ref}         — forwarded to the <select> (grid cell focus)
 *   ...rest                   — passed to the <select> (onKeyDown, onBlur, aria-*)
 */

import React from 'react';
import './FixedListSelect.css';

export function isOffList(value, options) {
  if (value == null || value === '') return false;
  return !(options || []).includes(value);
}

export function FixedListSelect({
  value,
  onChange,
  options = [],
  placeholder = '— Select —',
  className = '',
  disabled = false,
  layout = 'stack',
  selectRef,
  ...rest
}) {
  const list = options || [];
  const listReady = list.length > 0;
  const hasValue = value != null && value !== '';
  const offList = listReady && isOffList(value, list);
  // Only an on-list value is ever handed to the <select> (see header).
  const selectValue = hasValue && !offList && listReady ? value : '';

  function handleChange(e) {
    const next = e.target.value;
    if (next === '') return; // the placeholder is never a choice
    onChange(next);
  }

  return (
    <span className={`fls-wrap fls-${layout}`}>
      <select
        ref={selectRef}
        className={className}
        value={selectValue}
        onChange={handleChange}
        disabled={disabled || !listReady}
        {...rest}
      >
        <option value="" disabled>{placeholder}</option>
        {list.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      {hasValue && (offList || !listReady) && (
        <span className="fls-current" data-testid="fls-current">
          <span className="fls-current-label">Current:</span>{' '}
          <span className="fls-current-value">{value}</span>
          {offList && <span className="fls-current-note"> — not on the list</span>}
        </span>
      )}
    </span>
  );
}

export default FixedListSelect;
